/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

const useFeeList = () => {
  const { get, reset } = useAbortController();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const queryKey = useMemo(() => ['feelist', loanNumber, token], [loanNumber, token]);

  const getFeeData = useCallback(async () => {
    if (!loanNumber || !token) return { data: [] };

    try {
      const response = await axiosCall(
        { url: process.env.REACT_APP_FEE_LIST.replace('?', loanNumber), method: 'get' },
        reset(),
        {
          'Session-Token': token,
        },
      );

      if (!Array.isArray(response)) {
        throw new Error('Expected an array of fees');
      }

      return { data: response as FeeData[] };
    } catch (err: any) {
      console.error('Error fetching fee data:', err);
      return { data: [] };
    }
  }, [loanNumber, token, reset]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      return getFeeData();
    },
    refetchOnWindowFocus: false,
  });

  const discountFees = data?.data?.filter((f) => f.keyword === 'DISCOUNT');
  const totalDiscount = discountFees?.reduce((sum, f) => sum + f.totalAmount, 0);
  const borrowerDiscount = discountFees?.filter((f) => f.paidBy === 'B')?.reduce((sum, f) => sum + f.totalAmount, 0);

  const refetchFees = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  return {
    loadingFees: isFetching || isLoading,
    fees: data?.data ? data.data : [],
    totalDiscount,
    borrowerDiscount,
    refetchFees,
  };
};

export default useFeeList;
